import type { UIMessage } from "ai";
import { useCallback, useRef } from "react";

import type { paths } from "@phoenix/api/__generated__/v1";
import { authApiFetch } from "@phoenix/api/authApiFetch";
import { useAgentStore } from "@phoenix/contexts/AgentContext";

const SUMMARIZE_PATH =
  "/agents/{agent_id}/sessions/{session_id}/summary" as const;

export type SummarizeQuery =
  paths[typeof SUMMARIZE_PATH]["post"]["parameters"]["query"];

async function fetchSummary({
  sessionId,
  summarizeQuery,
  messages,
}: {
  sessionId: string;
  summarizeQuery: SummarizeQuery;
  messages: UIMessage[];
}): Promise<string> {
  const { data, response } = await authApiFetch.POST(SUMMARIZE_PATH, {
    params: {
      path: { agent_id: "assistant", session_id: sessionId },
      query: summarizeQuery,
    },
    body: { messages },
  });
  if (!response.ok || !data) {
    throw new Error(`summarize request failed: ${response.status}`);
  }
  return data.summary.trim();
}

/**
 * Hook that provides a function to asynchronously generate a session summary
 * after the first assistant response completes.
 *
 * The summary is generated by POSTing the session's full message history to
 * the dedicated session summary endpoint, which owns the prompt and the
 * structured-output tool schema. Fire-and-forget so the UI never blocks on it.
 */
export function useGenerateSessionSummary({
  summarizeQuery,
}: {
  summarizeQuery: SummarizeQuery;
}) {
  const store = useAgentStore();
  const requestedSessionsRef = useRef(new Set<string>());

  const generateSummary = useCallback(
    ({ sessionId }: { sessionId: string }) => {
      if (requestedSessionsRef.current.has(sessionId)) return;

      const state = store.getState();
      const session = state.sessionMap[sessionId];
      if (!session) return;
      if (session.shortSummary) return;
      if (session.messages.length === 0) return;

      requestedSessionsRef.current.add(sessionId);

      void fetchSummary({
        sessionId,
        summarizeQuery,
        messages: session.messages,
      })
        .then((summary) => {
          if (summary) {
            store.getState().updateSessionSummary(sessionId, summary);
          }
        })
        .catch(() => {
          requestedSessionsRef.current.delete(sessionId);
        });
    },
    [summarizeQuery, store]
  );

  return { generateSummary };
}
